import { useCallback, useEffect, useState } from 'react';

export interface IApplyIntent {
  vacancyId: string;
  companyName: string;
  position: string;
  applyUrl: string;
}

const STORAGE_KEY = 'career.applyIntent';

/**
 * 🔴 Намерение хранится в `sessionStorage`, а НЕ в кэше Apollo.
 *
 * Кэш нормализован и переживает вкладку: незакрытый вопрос всплыл бы на
 * следующем заходе, через неделю, про вакансию, о которой человек забыл. Сессия
 * браузера — ровно тот срок жизни, который у этого намерения есть на самом
 * деле.
 *
 * ⚠️ `try/catch` обязателен: в приватном окне и при запрете хранилища любое
 * обращение бросает, и уронить этим карточку вакансии было бы совсем нелепо.
 */
const read = (): IApplyIntent | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as IApplyIntent) : null;
  } catch {
    return null;
  }
};

const write = (intent: IApplyIntent | null) => {
  try {
    if (intent === null) {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(intent));
    }
  } catch {
    // Нечего делать: без хранилища вопрос просто не будет задан.
  }
};

/** Запомнить, что человек ушёл на сайт работодателя. */
export const rememberApplyIntent = (intent: IApplyIntent) => write(intent);

/**
 * Вопрос «вы отправили заявку?» после возврата с чужого сайта (ТЗ 0069).
 *
 * 🔴 Надёжного сигнала «человек откликнулся» НЕ СУЩЕСТВУЕТ: мы отпускаем его
 * на сайт работодателя и больше ничего не видим. Поэтому отклик НИКОГДА не
 * создаётся автоматически — только по явному ответу человека. Автоматически
 * проставленный отклик отравил бы и журнал, и оценку готовности, и объяснить
 * его пользователь не смог бы.
 *
 * Сигнал возврата — `visibilitychange` и `focus` вкладки ЛМС. `onblur` не
 * годится: он срабатывает и на переключении окна, и на клике в адресную
 * строку, то есть спрашивал бы у человека, который никуда не уходил.
 */
export const useApplyPrompt = () => {
  const [intent, setIntent] = useState<IApplyIntent | null>(null);

  useEffect(() => {
    const check = () => {
      // Человек здесь — если вкладка видима ЛИБО окно в фокусе.
      //
      // ⚠️ Одного `visibilityState` мало: у окна, ушедшего на второй план
      // целиком (другое приложение, второй монитор), состояние остаётся
      // `hidden`, даже когда вкладка снова активна и получила фокус. Поймано
      // проверкой в браузере: намерение лежало в хранилище, а вопрос не
      // задавался вовсе.
      if (document.visibilityState !== 'visible' && !document.hasFocus()) {
        return;
      }
      setIntent(read());
    };

    document.addEventListener('visibilitychange', check);
    window.addEventListener('focus', check);
    // Первая проверка после монтирования: человек мог вернуться на вкладку
    // раньше, чем экран успел смонтироваться.
    check();

    return () => {
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('focus', check);
    };
  }, []);

  const clear = useCallback(() => {
    write(null);
    setIntent(null);
  }, []);

  return { intent, clear };
};
